import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { FiArrowLeft, FiSave, FiPlus, FiTrash2 } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { makeRequestAuth } from "../../_config/api";

function CreateP2PSellOrder() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    walletType: "USDT",
    amount: "",
    rate: "",
  });

  const [paymentMethods, setPaymentMethods] = useState([
    {
      methodType: "UPI",
      label: "",
      isPrimary: false,
      details: {
        upiId: "",
      },
    },
  ]);

  // Handle standard input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Payment Method Handlers
  const handleAddPaymentMethod = () => {
    setPaymentMethods((prev) => [
      ...prev,
      {
        methodType: "BANK_TRANSFER",
        label: "",
        isPrimary: false,
        details: {
          accountHolderName: "",
          bankName: "",
          accountNumber: "",
          ifsc: "",
          branchName: "",
        },
      },
    ]);
  };

  const handleRemovePaymentMethod = (index) => {
    setPaymentMethods((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePaymentMethodChange = (index, field, value) => {
    const updatedMethods = [...paymentMethods];

    if (field === "methodType") {
      updatedMethods[index].methodType = value;

      // Reset details when type changes
      updatedMethods[index].details =
        value === "UPI"
          ? { upiId: "" }
          : {
              accountHolderName: "",
              bankName: "",
              accountNumber: "",
              ifsc: "",
              branchName: "",
            };
    } else if (field === "isPrimary") {
      updatedMethods[index].isPrimary = value;
    } else if (field === "label") {
      updatedMethods[index].label = value;
    } else {
      // Handle details fields
      updatedMethods[index].details = {
        ...updatedMethods[index].details,
        [field]: value,
      };
    }

    setPaymentMethods(updatedMethods);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (!formData.name.trim()) return showErrorToast("Name is required");
    if (!formData.amount || formData.amount <= 0)
      return showErrorToast("Valid amount is required");
    if (!formData.rate || formData.rate <= 0)
      return showErrorToast("Valid rate is required");
    if (paymentMethods.length === 0)
      return showErrorToast("Add at least one payment method");

    const payload = {
      name: formData.name,
      walletType: formData.walletType,
      amount: Number(formData.amount),
      rate: Number(formData.rate),
      paymentMethods: paymentMethods.map((pm) => ({
        methodType: pm.methodType,
        label: pm.label,
        isPrimary: pm.isPrimary,
        details: pm.details,
      })),
    };

    try {
      setSubmitting(true);
      const response = await makeRequestAuth(
        "post",
        "/admin/createP2PSellOrder",
        payload,
      );

      if (
        response.status === true ||
        response.success === true ||
        response.data
      ) {
        showSuccessToast("Sell order created successfully!");
        setTimeout(() => navigate("/p2p/sell-order-list"), 1500);
      } else {
        showErrorToast(response.message || "Failed to create order");
      }
    } catch (err) {
      console.error("Error creating order:", err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="bg-white dark:bg-[#1E2738] rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 border-b border-gray-200 dark:border-gray-700 pb-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Create Admin P2P Sell Order
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new sell order listing for users.
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-purple-600 transition"
          >
            <FiArrowLeft size={20} /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Main Order Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Order Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Quick USDT Sell"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Wallet Type <span className="text-red-500">*</span>
              </label>
              <select
                name="walletType"
                value={formData.walletType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition cursor-pointer"
              >
                <option value="USDT">USDT</option>
                <option value="TOKEN">TOKEN</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                placeholder="e.g., 500"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Rate (Price per unit) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="rate"
                min="0"
                step="0.01"
                value={formData.rate}
                onChange={handleChange}
                placeholder="e.g., 88.50"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                Payment Methods Supported
              </h3>
              <button
                type="button"
                onClick={handleAddPaymentMethod}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition"
              >
                <FiPlus /> Add Method
              </button>
            </div>

            <div className="space-y-4">
              {paymentMethods.map((method, index) => (
                <div
                  key={index}
                  className="p-5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 relative"
                >
                  {/* Remove Button */}
                  {paymentMethods.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePaymentMethod(index)}
                      className="absolute top-4 right-4 text-red-500 hover:text-red-700 dark:hover:text-red-400 p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                      title="Remove Payment Method"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Method Type Select */}
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Method Type
                      </label>
                      <select
                        value={method.methodType}
                        onChange={(e) =>
                          handlePaymentMethodChange(
                            index,
                            "methodType",
                            e.target.value,
                          )
                        }
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                      >
                        <option value="UPI">UPI</option>
                        <option value="BANK_TRANSFER">Bank Transfer</option>
                      </select>
                    </div>

                    {/* Label Input */}
                    {method.methodType === "UPI" && (
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Holder Name
                        </label>
                        <input
                          type="text"
                          value={method.label}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "label",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., Personal Account"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                        />
                      </div>
                    )}

                    {/* --- DYNAMIC FIELDS START --- */}
                    {method.methodType === "UPI" ? (
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          UPI ID
                        </label>
                        <input
                          type="text"
                          value={method.details.upiId || ""}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "upiId",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="example@upi"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                          required
                        />
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Account Holder Name
                          </label>
                          <input
                            type="text"
                            value={method?.details?.accountHolderName || ""}
                            onChange={(e) =>
                              handlePaymentMethodChange(
                                index,
                                "accountHolderName",
                                e.target.value,
                                true,
                              )
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={method?.details?.bankName || ""}
                            onChange={(e) =>
                              handlePaymentMethodChange(
                                index,
                                "bankName",
                                e.target.value,
                                true,
                              )
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            placeholder="HDFC Bank"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={method?.details?.accountNumber || ""}
                            onChange={(e) =>
                              handlePaymentMethodChange(
                                index,
                                "accountNumber",
                                e.target.value,
                                true,
                              )
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            placeholder="1234567890"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            IFSC Code
                          </label>
                          <input
                            type="text"
                            value={method?.details?.ifsc || ""}
                            onChange={(e) =>
                              handlePaymentMethodChange(
                                index,
                                "ifsc",
                                e.target.value,
                                true,
                              )
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            placeholder="HDFC0001234"
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Branch Name
                          </label>
                          <input
                            type="text"
                            value={method?.details?.branchName || ""}
                            onChange={(e) =>
                              handlePaymentMethodChange(
                                index,
                                "branchName",
                                e.target.value,
                                true,
                              )
                            }
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            placeholder="Main Street Branch"
                          />
                        </div>
                      </>
                    )}
                    {/* --- DYNAMIC FIELDS END --- */}
                  </div>

                  {/* Is Primary Checkbox */}
                  {/* <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id={`primary-${index}`}
                      checked={method.isPrimary}
                      onChange={(e) =>
                        handlePaymentMethodChange(
                          index,
                          "isPrimary",
                          e.target.checked,
                        )
                      }
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    />
                    <label
                      htmlFor={`primary-${index}`}
                      className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      Mark as Primary Method
                    </label>
                  </div> */}
                </div>
              ))}
            </div>
          </div>

          {/* Submit Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
            >
              {submitting ? (
                "Processing..."
              ) : (
                <>
                  <FiSave /> Publish Sell Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default CreateP2PSellOrder;
